import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ErroreApi, chiama } from './cliente.js';
import { t } from '../i18n/lingua.js';

/**
 * Gli allegati dell'ordine.
 *
 * Sono i file appesi all'ordine e a nient'altro: la conferma dello
 * spedizioniere, la distinta scannerizzata, il messaggio di Outlook salvato.
 * Nell'archivio vero ce ne sono 13.437 e l'ultimo è del 5 agosto: è roba che
 * si usa tutti i giorni, e nella v3 finora si contava soltanto.
 */

export interface AllegatoOrdine {
  id: number;
  title: string;
  fileName: string | null;
  notes: string | null;
  archived: boolean;
  createdAt: string;
  fileSize: number | null;
  /** Con che nome esce quando lo si scarica. */
  downloadName: string;
}

export function useAllegatiOrdine(ordineId: number) {
  return useQuery({
    queryKey: ['allegati-ordine', ordineId],
    queryFn: () =>
      chiama<{ attachments: AllegatoOrdine[] }>(`/api/orders/${ordineId}/attachments`),
    enabled: Number.isInteger(ordineId) && ordineId > 0,
  });
}

/**
 * Attacca un file.
 *
 * Non passa da `chiama` perché quello mette `content-type: application/json`,
 * e con un multipart il confine lo deve scrivere il browser.
 */
export function useAttaccaFile(ordineId: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, title }: { file: File; title?: string }) => {
      const dati = new FormData();
      // il titolo va **prima** del file: fastify legge i campi in ordine, e
      // quelli che arrivano dopo il file non si vedono più
      if (title) dati.append('title', title);
      dati.append('file', file);

      const risposta = await fetch(`/api/orders/${ordineId}/attachments`, {
        method: 'POST',
        body: dati,
        credentials: 'same-origin',
      });
      const corpo = await risposta.json().catch(() => null);
      if (!risposta.ok) {
        throw new ErroreApi(
          corpo?.error ?? 'erroreCaricamento',
          corpo?.message ?? t('Il caricamento non è riuscito.'),
          risposta.status,
        );
      }
      return corpo as AllegatoOrdine;
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['allegati-ordine', ordineId] });
      void client.invalidateQueries({ queryKey: ['ordine', ordineId] });
    },
  });
}

export function useRinominaAllegato(ordineId: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...dati }: { id: number; title?: string; notes?: string | null }) =>
      chiama<AllegatoOrdine>(`/api/orders/${ordineId}/attachments/${id}`, {
        metodo: 'PATCH',
        corpo: dati,
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['allegati-ordine', ordineId] }),
  });
}

export function useTogliAllegato(ordineId: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      chiama<{ deleted: boolean }>(`/api/orders/${ordineId}/attachments/${id}`, {
        metodo: 'DELETE',
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['allegati-ordine', ordineId] });
      void client.invalidateQueries({ queryKey: ['ordine', ordineId] });
    },
  });
}

/** L'indirizzo da cui scaricare: il browser ci va da solo, col cookie. */
export function indirizzoAllegato(ordineId: number, allegatoId: number): string {
  return `/api/orders/${ordineId}/attachments/${allegatoId}/download`;
}
